import { useState, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useCart } from '../../hooks/useCart';

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showCartPreview, setShowCartPreview] = useState(false);
  const location = useLocation();
  const { cart } = useCart();

  const isActive = (path: string) => location.pathname === path;

  const handleCartHover = useCallback(() => {
    if (cart.items && cart.items.length > 0) {
      setShowCartPreview(true);
    }
  }, [cart.items]);

  const handleCartLeave = useCallback(() => {
    setShowCartPreview(false);
  }, []);

  return (
    <>
      <header className="bg-[#FAF9F8] shadow-sm border-b-2 border-[#C49B86]/20 sticky top-0 z-50" data-test="site-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-3 sm:py-4">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-2 sm:space-x-3 group">
              <div className="transition-transform duration-300 group-hover:scale-105">
                <img
                  src="https://static.readdy.ai/image/fd456b0d540e8f517714341c204b3126/e5a40b4f7b52fcfa548a8f38ffe25152.png"
                  alt="Les Carnets de Lolotte - Logo"
                  className="h-12 w-auto sm:h-16 md:h-20"
                />
              </div>
              <div className="hidden sm:block">
                <p
                  className="text-xs sm:text-sm text-[#C49B86] italic"
                  style={{ fontFamily: 'Playfair Display, serif' }}
                >
                  Créations poétiques en livres pliés
                </p>
              </div>
            </Link>

            {/* Navigation desktop */}
            <nav className="hidden md:flex items-center space-x-6 lg:space-x-8">
              <Link 
                to="/" 
                className="text-sm lg:text-base font-medium transition-colors hover:text-[#B97A72] hover:underline text-[#6B5E5A]"
                style={{ fontFamily: '"Playfair Display", serif' }}
              >
                Accueil
              </Link>
              <Link 
                to="/boutique" 
                className="text-sm lg:text-base font-medium transition-colors hover:text-[#B97A72] hover:underline text-[#6B5E5A]"
                style={{ fontFamily: '"Playfair Display", serif' }}
              >
                Boutique
              </Link>
              <Link 
                to="/pourquoi-offrir" 
                className="text-sm lg:text-base font-medium transition-colors hover:text-[#B97A72] hover:underline text-[#6B5E5A]"
                style={{ fontFamily: '"Playfair Display", serif' }}
              >
                Pourquoi offrir ?
              </Link>
              <Link 
                to="/about" 
                className="text-sm lg:text-base font-medium transition-colors hover:text-[#B97A72] hover:underline text-[#6B5E5A]"
                style={{ fontFamily: '"Playfair Display", serif' }}
              >
                À propos
              </Link>
              <Link 
                to="/contact" 
                className="text-sm lg:text-base font-medium transition-colors hover:text-[#B97A72] hover:underline text-[#6B5E5A]"
                style={{ fontFamily: '"Playfair Display", serif' }}
              >
                Contact
              </Link>
            </nav>

            {/* Panier et menu mobile */}
            <div className="flex items-center space-x-3 sm:space-x-4">
              {/* Icône panier */}
              <div className="relative">
                <Link
                  to="/panier"
                  className="relative p-2 text-[#C49B86] hover:text-[#B97A72] transition-colors"
                  onMouseEnter={handleCartHover}
                  onMouseLeave={handleCartLeave}
                >
                  <div className="w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center">
                    <i className="ri-shopping-bag-line text-xl sm:text-2xl"></i>
                  </div>
                  {cart.itemCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-[#E8B4B8] text-white text-xs rounded-full w-4 h-4 sm:w-5 sm:h-5 flex items-center justify-center font-bold border-2 border-white shadow-lg">
                      {cart.itemCount}
                    </span>
                  )}
                </Link>

                {/* Aperçu rapide du panier */}
                {showCartPreview && cart.items && cart.items.length > 0 && (
                  <div
                    className="absolute right-0 top-full mt-2 w-72 sm:w-80 bg-white rounded-lg shadow-xl border-2 border-[#C49B86]/20 p-3 sm:p-4 z-50"
                    onMouseEnter={() => setShowCartPreview(true)}
                    onMouseLeave={handleCartLeave}
                  >
                    <h3
                      className="text-base sm:text-lg font-semibold text-[#C49B86] mb-2 sm:mb-3"
                      style={{ fontFamily: 'Playfair Display, serif' }}
                    >
                      Votre panier ({cart.itemCount} article{cart.itemCount > 1 ? 's' : ''})
                    </h3>
                    <div className="space-y-2 sm:space-y-3 max-h-60 overflow-y-auto">
                      {cart.items.slice(0, 3).map((item) => (
                        <div
                          key={`preview-${item.id}-${item.name}`}
                          className="flex items-center space-x-2 sm:space-x-3"
                        >
                          <img
                            src={item.image || 'https://via.placeholder.com/48x48?text=Produit'}
                            alt={item.name}
                            className="w-10 h-10 sm:w-12 sm:h-12 object-cover rounded border border-[#C49B86]"
                          />
                          <div className="flex-1">
                            <p
                              className="text-xs sm:text-sm font-medium text-[#6B5E5A]"
                              style={{ fontFamily: 'Playfair Display, serif' }}
                            >
                              {item.name}
                            </p>
                            <p
                              className="text-xs text-[#C49B86]"
                              style={{ fontFamily: 'Poppins, sans-serif' }}
                            >
                              {item.quantity} × {item.price}€
                            </p>
                          </div>
                        </div>
                      ))}
                      {cart.items.length > 3 && (
                        <p
                          className="text-xs text-[#6B5E5A] text-center italic"
                          style={{ fontFamily: 'Poppins, sans-serif' }}
                        >
                          ... et {cart.items.length - 3} autre
                          {cart.items.length - 3 > 1 ? 's' : ''} article
                          {cart.items.length - 3 > 1 ? 's' : ''}
                        </p>
                      )}
                    </div>
                    <div className="mt-3 sm:mt-4 pt-2 sm:pt-3 border-t border-[#C49B86]/20">
                      <div className="flex justify-between items-center mb-2 sm:mb-3">
                        <span
                          className="font-medium text-[#6B5E5A] text-sm"
                          style={{ fontFamily: 'Playfair Display, serif' }}
                        >
                          Total :
                        </span>
                        <span
                          className="text-base sm:text-lg font-bold text-[#C49B86]"
                          style={{ fontFamily: 'Playfair Display, serif' }}
                        >
                          {cart.total?.toFixed(2)}€
                        </span>
                      </div>
                      <Link
                        to="/panier"
                        className="w-full bg-[#C49B86] text-white px-3 sm:px-4 py-2 rounded-full text-center font-semibold hover:bg-[#B97A72] transition-colors block whitespace-nowrap text-xs sm:text-sm"
                        style={{ fontFamily: 'Playfair Display, serif' }}
                      >
                        Voir mon panier
                      </Link>
                    </div>
                  </div>
                )}
              </div>

              {/* Menu hamburger mobile */}
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="md:hidden p-2 text-[#C49B86] hover:text-[#B97A72] transition-colors"
                type="button"
              >
                <i className={`text-xl sm:text-2xl ${isMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
              </button>
            </div>
          </div>

          {/* Menu mobile */}
          {isMenuOpen && (
            <div className="md:hidden absolute top-full left-0 right-0 bg-white shadow-lg border-t border-[#F8EDEA] z-50">
              <div className="px-4 py-4 space-y-3 sm:space-y-4">
                <Link 
                  to="/" 
                  className="block text-sm sm:text-base font-medium text-[#6B5E5A] hover:text-[#B97A72] transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                  style={{ fontFamily: '"Playfair Display", serif' }}
                >
                  Accueil
                </Link>
                <Link 
                  to="/boutique" 
                  className="block text-sm sm:text-base font-medium text-[#6B5E5A] hover:text-[#B97A72] transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                  style={{ fontFamily: '"Playfair Display", serif' }}
                >
                  Boutique
                </Link>
                <Link 
                  to="/pourquoi-offrir" 
                  className="block text-sm sm:text-base font-medium text-[#6B5E5A] hover:text-[#B97A72] transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                  style={{ fontFamily: '"Playfair Display", serif' }}
                >
                  Pourquoi offrir ?
                </Link>
                <Link 
                  to="/about" 
                  className="block text-sm sm:text-base font-medium text-[#6B5E5A] hover:text-[#B97A72] transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                  style={{ fontFamily: '"Playfair Display", serif' }}
                >
                  À propos
                </Link>
                <Link 
                  to="/contact" 
                  className="block text-sm sm:text-base font-medium text-[#6B5E5A] hover:text-[#B97A72] transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                  style={{ fontFamily: '"Playfair Display", serif' }}
                >
                  Contact
                </Link>
                <Link
                  to="/panier"
                  onClick={() => setIsMenuOpen(false)}
                  className={`text-sm font-medium transition-colors hover:text-[#B97A72] flex items-center ${
                    isActive('/panier') ? 'text-[#C49B86]' : 'text-[#6B5E5A]'
                  }`}
                  style={{ fontFamily: 'Playfair Display, serif' }}
                >
                  <i className="ri-shopping-bag-line mr-2"></i>
                  Mon panier ({cart.itemCount})
                </Link>
              </div>
            </div>
          )}
        </div>
      </header>
    </>
  );
}
